import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  IconButton,
  Tooltip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  LinearProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Assessment,
  Download,
  Print,
  Share,
  Visibility,
  PictureAsPdf,
  TableChart,
  BarChart,
  PieChart,
  DonutLarge,
  Timeline,
  FilterList,
  DateRange,
  School,
  People,
  Male,
  Female,
  Transgender,
  Wc,
  MenuBook,
  CalendarToday,
  FileDownload,
  CloudDownload,
  Email,
  WhatsApp,
  Facebook,
  Twitter,
  Link as LinkIcon,
  ExpandMore,
  ExpandLess,
  CheckCircle,
  Pending,
  Cancel,
  HourglassEmpty,
  TrendingUp,
  TrendingDown,
  Equalizer,
  Analytics,
  Description,
  Save,
  Refresh,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  PointElement,
  LineElement,
  Filler,
  RadialLinearScale,
} from 'chart.js';
import { Pie, Doughnut, Bar, Line, Radar } from 'react-chartjs-2';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import api from '../services/api';

// Register ChartJS components
ChartJS.register(
  ArcElement,
  ChartTooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  PointElement,
  LineElement,
  Filler,
  RadialLinearScale
);

// ── School color palette (matches ApplicationForm) ──────────────────────────
const schoolColors = {
  primary:   '#1a237e',
  secondary: '#0d47a1',
  accent:    '#ff6f00',
  gold:      '#ffd700',
  dark:      '#0a0a2a',
  light:     '#e3f2fd',
  white:     '#ffffff',
  textDark:  '#1a1a2e',
  textLight: '#ffffff',
};

// ── Styled components ────────────────────────────────────────────────────────

// Summary stat cards – keep dynamic colour prop for status tinting
const StyledCard = styled(Card)(({ theme, color }) => ({
  background: `linear-gradient(135deg, ${color}25, ${color}08)`,
  backdropFilter: 'blur(10px)',
  borderRadius: 20,
  border: `1px solid ${color}40`,
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: `0 10px 30px ${color}35`,
  },
}));

// Glass panel used for filter bar, charts, and table
const GlassCard = styled(Card)(({ theme }) => ({
  background: schoolColors.white,
  backdropFilter: 'blur(20px)',
  borderRadius: 24,
  border: `1px solid ${schoolColors.primary}15`,
  boxShadow: `0 8px 32px ${schoolColors.primary}15`,
}));

// Primary action button (matches StyledButton in ApplicationForm)
const SchoolButton = styled(Button)(({ theme, variant: v }) => ({
  borderRadius: 8,
  padding: '10px 24px',
  textTransform: 'none',
  fontWeight: 600,
  ...(v === 'contained' && {
    background: `linear-gradient(135deg, ${schoolColors.primary}, ${schoolColors.secondary})`,
    color: schoolColors.white,
    '&:hover': {
      background: `linear-gradient(135deg, ${schoolColors.secondary}, ${schoolColors.primary})`,
    },
  }),
  ...(v === 'outlined' && {
    borderColor: schoolColors.primary,
    color: schoolColors.primary,
    '&:hover': {
      borderColor: schoolColors.accent,
      color: schoolColors.accent,
      background: `${schoolColors.accent}08`,
    },
  }),
}));

// Accent button for PDF export
const AccentButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  padding: '10px 24px',
  textTransform: 'none',
  fontWeight: 600,
  background: `linear-gradient(135deg, ${schoolColors.accent}, #e65100)`,
  color: schoolColors.white,
  '&:hover': {
    background: `linear-gradient(135deg, #e65100, ${schoolColors.accent})`,
  },
}));

// ── Component ────────────────────────────────────────────────────────────────
const Reports = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [reportType, setReportType]     = useState('summary');
  const [dateRange, setDateRange]       = useState({
    start: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
    end:   new Date().toISOString().split('T')[0],
  });
  const [selectedForm,   setSelectedForm]   = useState('all');
  const [selectedGender, setSelectedGender] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [expandedSections, setExpandedSections] = useState({ summary: true, charts: true, tables: true });
  const [page, setPage]               = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [stats, setStats] = useState({
    total: 0, pending: 0, approved: 0, rejected: 0, waiting_list: 0,
    byGender: { male: 0, female: 0 },
    byForm: { form1: 0, form2: 0, form3: 0, form4: 0, form5: 0 },
    byStatus: {}, monthlyTrend: [], weeklyTrend: [],
  });

  useEffect(() => { fetchApplications(); }, []);

  const fetchApplications = async () => {
    try {
      const response = await api.get('/admin/applications/');
      setApplications(response.data);
      calculateDetailedStats(response.data);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
      setSnackbar({ open: true, message: 'Failed to fetch application data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const calculateDetailedStats = (data) => {
    const computed = {
      total: data.length, pending: 0, approved: 0, rejected: 0, waiting_list: 0,
      byGender: { male: 0, female: 0 },
      byForm: { form1: 0, form2: 0, form3: 0, form4: 0, form5: 0 },
      byStatus: {}, monthlyTrend: {}, weeklyTrend: {},
    };

    data.forEach(app => {
      computed[app.status] = (computed[app.status] || 0) + 1;
      computed.byStatus[app.status] = (computed.byStatus[app.status] || 0) + 1;
      if (app.gender === 'ME') computed.byGender.male++;
      else if (app.gender === 'KE') computed.byGender.female++;
      const level = app.applying_for_level;
      if (computed.byForm[level] !== undefined) computed.byForm[level]++;
      const date      = new Date(app.submitted_date);
      const monthYear = `${date.toLocaleString('default', { month: 'short' })} ${date.getFullYear()}`;
      computed.monthlyTrend[monthYear] = (computed.monthlyTrend[monthYear] || 0) + 1;
      const weekLabel = `Week ${getWeekNumber(date)}`;
      computed.weeklyTrend[weekLabel] = (computed.weeklyTrend[weekLabel] || 0) + 1;
    });

    setStats(computed);
  };

  const getWeekNumber = (date) => {
    const firstDayOfYear  = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear  = (date - firstDayOfYear) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
  };

  const filterApplications = () =>
    applications.filter(app => {
      const date      = new Date(app.submitted_date);
      const dateMatch = date >= new Date(dateRange.start) && date <= new Date(dateRange.end);
      const formMatch   = selectedForm   === 'all' || app.applying_for_level === selectedForm;
      const genderMatch = selectedGender === 'all' ||
        (selectedGender === 'male'   && app.gender === 'ME') ||
        (selectedGender === 'female' && app.gender === 'KE');
      const statusMatch = selectedStatus === 'all' || app.status === selectedStatus;
      return dateMatch && formMatch && genderMatch && statusMatch;
    });

  const filteredApplications = filterApplications();

  // ── Chart data ─────────────────────────────────────────────────────────────
  // Using school colours for primary/accent accents
  const genderChartData = {
    labels: ['Male', 'Female'],
    datasets: [{
      data: [stats.byGender.male, stats.byGender.female],
      backgroundColor: [`${schoolColors.secondary}cc`, `${schoolColors.accent}cc`],
      borderColor:     [schoolColors.secondary, schoolColors.accent],
      borderWidth: 2,
    }],
  };

  const formChartData = {
    labels: ['Form 1', 'Form 2', 'Form 3', 'Form 4', 'Form 5'],
    datasets: [{
      label: 'Applications by Form',
      data: [stats.byForm.form1, stats.byForm.form2, stats.byForm.form3, stats.byForm.form4, stats.byForm.form5],
      backgroundColor: [
        `${schoolColors.primary}cc`,
        `${schoolColors.secondary}cc`,
        `${schoolColors.accent}cc`,
        `${schoolColors.gold}cc`,
        '#0d47a1aa',
      ],
      borderRadius: 8,
    }],
  };

  const statusChartData = {
    labels: ['Pending', 'Approved', 'Rejected', 'Waiting List'],
    datasets: [{
      data: [stats.pending, stats.approved, stats.rejected, stats.waiting_list],
      backgroundColor: [
        `${schoolColors.gold}cc`,
        '#4caf50cc',
        '#f44336cc',
        `${schoolColors.secondary}cc`,
      ],
      borderWidth: 2,
    }],
  };

  const monthlyTrendData = {
    labels: Object.keys(stats.monthlyTrend),
    datasets: [{
      label: 'Applications',
      data: Object.values(stats.monthlyTrend),
      fill: true,
      backgroundColor: `${schoolColors.primary}18`,
      borderColor: schoolColors.primary,
      tension: 0.4,
      pointBackgroundColor: schoolColors.accent,
      pointBorderColor: schoolColors.white,
      pointBorderWidth: 2,
      pointRadius: 5,
      pointHoverRadius: 8,
    }],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { font: { size: 12, family: "'Roboto', sans-serif" }, padding: 20 },
      },
      tooltip: {
        backgroundColor: schoolColors.dark,
        titleFont: { size: 14, weight: 'bold' },
        bodyFont: { size: 12 },
        padding: 12,
        cornerRadius: 8,
      },
    },
    animation: { duration: 2000, easing: 'easeInOutQuart' },
  };

  // ── Export handlers ────────────────────────────────────────────────────────
  const handleExportExcel = () => {
    try {
      const exportData = filteredApplications.map(app => ({
        'Registration Number': app.registration_number,
        'First Name':          app.first_name,
        'Middle Name':         app.middle_name || '',
        'Last Name':           app.last_name,
        'Gender':              app.gender === 'ME' ? 'Male' : 'Female',
        'Date of Birth':       new Date(app.date_of_birth).toLocaleDateString(),
        'Applying For':        app.applying_for_level?.replace('form', 'Form '),
        'Status':              app.status.replace('_', ' ').toUpperCase(),
        'Submission Date':     new Date(app.submitted_date).toLocaleDateString(),
        'Ward':                app.ward,
        'District':            app.district,
        'Region':              app.region,
        'Religion':            app.religion,
        'Primary School':      app.primary_school,
        'Exam Number':         app.exam_number,
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Applications');
      const colWidths = [];
      for (let i = 0; i < exportData.length; i++) {
        Object.keys(exportData[i]).forEach((key, idx) => {
          const len = Math.max(key.length, String(exportData[i][key]).length);
          colWidths[idx] = Math.max(colWidths[idx] || 0, Math.min(len, 50));
        });
      }
      ws['!cols'] = colWidths.map(width => ({ width }));
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      saveAs(
        new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
        `kaozya_applications_report_${new Date().toISOString().split('T')[0]}.xlsx`
      );
      setSnackbar({ open: true, message: 'Excel report generated successfully!', severity: 'success' });
    } catch (error) {
      console.error('Failed to export Excel:', error);
      setSnackbar({ open: true, message: 'Failed to generate Excel report', severity: 'error' });
    }
  };

  const handleExportPDF = () => {
    try {
      const doc = new jsPDF();
      // Header in school primary colour
      doc.setFontSize(20);
      doc.setTextColor(26, 35, 126); // schoolColors.primary as RGB
      doc.text('Kaozya Secondary School', 105, 15, { align: 'center' });
      doc.setFontSize(16);
      doc.setTextColor(255, 111, 0); // schoolColors.accent as RGB
      doc.text('Applications Report', 105, 25, { align: 'center' });
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(`Report Period: ${dateRange.start} to ${dateRange.end}`, 105, 35, { align: 'center' });

      doc.setFontSize(12);
      doc.setTextColor(26, 35, 126);
      doc.text('Summary Statistics', 14, 45);

      doc.autoTable({
        startY: 50,
        head: [['Metric', 'Count']],
        body: [
          ['Total Applications', stats.total],
          ['Pending',            stats.pending],
          ['Approved',           stats.approved],
          ['Rejected',           stats.rejected],
          ['Waiting List',       stats.waiting_list],
          ['Male',               stats.byGender.male],
          ['Female',             stats.byGender.female],
        ],
        theme: 'striped',
        headStyles: { fillColor: [26, 35, 126] }, // primary
      });

      const tableData = filteredApplications.slice(0, 50).map(app => [
        app.registration_number,
        `${app.first_name} ${app.last_name}`,
        app.gender === 'ME' ? 'Male' : 'Female',
        app.applying_for_level?.replace('form', 'Form '),
        app.status.replace('_', ' ').toUpperCase(),
        new Date(app.submitted_date).toLocaleDateString(),
      ]);

      doc.autoTable({
        startY: doc.lastAutoTable.finalY + 10,
        head: [['Reg No.', 'Name', 'Gender', 'Form', 'Status', 'Date']],
        body: tableData,
        theme: 'striped',
        headStyles: { fillColor: [26, 35, 126] },
        columnStyles: { 0: { cellWidth: 30 }, 1: { cellWidth: 40 }, 2: { cellWidth: 20 }, 3: { cellWidth: 20 }, 4: { cellWidth: 25 }, 5: { cellWidth: 25 } },
      });

      doc.save(`kaozya_report_${new Date().toISOString().split('T')[0]}.pdf`);
      setSnackbar({ open: true, message: 'PDF report generated successfully!', severity: 'success' });
    } catch (error) {
      console.error('Failed to export PDF:', error);
      setSnackbar({ open: true, message: 'Failed to generate PDF report', severity: 'error' });
    }
  };

  const toggleSection = (section) =>
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));

  const getStatusChip = (status) => {
    const config = {
      pending:      { color: 'warning', icon: <Pending />,      label: 'Pending' },
      approved:     { color: 'success', icon: <CheckCircle />,  label: 'Approved' },
      rejected:     { color: 'error',   icon: <Cancel />,       label: 'Rejected' },
      waiting_list: { color: 'info',    icon: <HourglassEmpty />, label: 'Waiting List' },
    }[status] || { color: 'default', icon: null, label: status };

    return (
      <Chip
        size="small"
        label={config.label}
        color={config.color}
        icon={config.icon}
        sx={{ fontWeight: 600 }}
      />
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>

      {/* ── Page header with school banner style ── */}
      <Box
        sx={{
          mb: 4,
          p: 3,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${schoolColors.primary}, ${schoolColors.secondary})`,
          boxShadow: `0 10px 30px ${schoolColors.primary}40`,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 700, color: schoolColors.white, mb: 0.5 }}>
          Reports &amp; Analytics
        </Typography>
        <Typography variant="body1" sx={{ color: schoolColors.gold }}>
          Kaozya Secondary School — Comprehensive Application Analysis
        </Typography>
      </Box>

      {/* ── Filters ── */}
      <GlassCard sx={{ mb: 4 }}>
        <CardContent>
          {/* Filter label */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <FilterList sx={{ color: schoolColors.primary }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: schoolColors.primary }}>
              Filter Applications
            </Typography>
          </Box>

          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                InputLabelProps={{ shrink: true }}
                InputProps={{ startAdornment: <CalendarToday sx={{ mr: 1, color: schoolColors.primary, fontSize: 20 }} /> }}
                sx={{
                  '& label.Mui-focused':            { color: schoolColors.primary },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: schoolColors.primary },
                  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: schoolColors.accent },
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                InputLabelProps={{ shrink: true }}
                InputProps={{ startAdornment: <CalendarToday sx={{ mr: 1, color: schoolColors.primary, fontSize: 20 }} /> }}
                sx={{
                  '& label.Mui-focused':            { color: schoolColors.primary },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: schoolColors.primary },
                  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: schoolColors.accent },
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth sx={{ '& label.Mui-focused': { color: schoolColors.primary }, '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: schoolColors.primary } }}>
                <InputLabel>Form Level</InputLabel>
                <Select value={selectedForm} onChange={(e) => setSelectedForm(e.target.value)} label="Form Level">
                  <MenuItem value="all">All Forms</MenuItem>
                  <MenuItem value="form1">Form 1</MenuItem>
                  <MenuItem value="form2">Form 2</MenuItem>
                  <MenuItem value="form3">Form 3</MenuItem>
                  <MenuItem value="form4">Form 4</MenuItem>
                  <MenuItem value="form5">Form 5</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth sx={{ '& label.Mui-focused': { color: schoolColors.primary }, '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: schoolColors.primary } }}>
                <InputLabel>Gender</InputLabel>
                <Select value={selectedGender} onChange={(e) => setSelectedGender(e.target.value)} label="Gender">
                  <MenuItem value="all">All Genders</MenuItem>
                  <MenuItem value="male">Male</MenuItem>
                  <MenuItem value="female">Female</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth sx={{ '& label.Mui-focused': { color: schoolColors.primary }, '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: schoolColors.primary } }}>
                <InputLabel>Status</InputLabel>
                <Select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} label="Status">
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                  <MenuItem value="waiting_list">Waiting List</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
            <SchoolButton
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => {
                setDateRange({
                  start: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
                  end:   new Date().toISOString().split('T')[0],
                });
                setSelectedForm('all');
                setSelectedGender('all');
                setSelectedStatus('all');
              }}
            >
              Reset Filters
            </SchoolButton>
            <SchoolButton variant="contained" startIcon={<FileDownload />} onClick={handleExportExcel}>
              Export Excel
            </SchoolButton>
            <AccentButton startIcon={<PictureAsPdf />} onClick={handleExportPDF}>
              Export PDF
            </AccentButton>
          </Box>
        </CardContent>
      </GlassCard>

      {/* ── Summary stat cards ── */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[
          { delay: 0.1, color: schoolColors.primary,   icon: <Assessment />, value: filteredApplications.length, label: 'Filtered Applications', ratio: filteredApplications.length / (stats.total || 1) },
          { delay: 0.2, color: '#4caf50',               icon: <CheckCircle />, value: stats.approved,             label: 'Approved Applications',  ratio: stats.approved / (stats.total || 1) },
          { delay: 0.3, color: schoolColors.accent,     icon: <Pending />,     value: stats.pending,              label: 'Pending Applications',   ratio: stats.pending / (stats.total || 1) },
          { delay: 0.4, color: '#f44336',               icon: <Cancel />,      value: stats.rejected,             label: 'Rejected Applications',  ratio: stats.rejected / (stats.total || 1) },
        ].map(({ delay, color, icon, value, label, ratio }) => (
          <Grid item xs={12} md={3} key={label}>
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay }}>
              <StyledCard color={color}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Avatar sx={{ bgcolor: `${color}25`, color, width: 56, height: 56 }}>
                      {icon}
                    </Avatar>
                    <Typography variant="h3" sx={{ fontWeight: 700, color }}>
                      {value}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {label}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={ratio * 100}
                    sx={{
                      mt: 2, height: 6, borderRadius: 3,
                      bgcolor: `${color}18`,
                      '& .MuiLinearProgress-bar': { bgcolor: color },
                    }}
                  />
                </CardContent>
              </StyledCard>
            </motion.div>
          </Grid>
        ))}
      </Grid>

      {/* ── Charts ── */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Gender doughnut */}
        <Grid item xs={12} md={6}>
          <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 }}>
            <GlassCard>
              <CardHeader
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Wc sx={{ color: schoolColors.primary }} />
                    <Typography variant="h6" sx={{ color: schoolColors.primary, fontWeight: 600 }}>Gender Distribution</Typography>
                  </Box>
                }
                action={
                  <IconButton onClick={() => toggleSection('charts')} sx={{ color: schoolColors.primary }}>
                    {expandedSections.charts ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                }
              />
              <Collapse in={expandedSections.charts}>
                <CardContent>
                  <Box sx={{ height: 300 }}>
                    <Doughnut data={genderChartData} options={chartOptions} />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, mt: 2 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" sx={{ color: schoolColors.secondary }}>{stats.byGender.male}</Typography>
                      <Typography variant="body2">Male</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" sx={{ color: schoolColors.accent }}>{stats.byGender.female}</Typography>
                      <Typography variant="body2">Female</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Collapse>
            </GlassCard>
          </motion.div>
        </Grid>

        {/* Form bar chart */}
        <Grid item xs={12} md={6}>
          <motion.div initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.6 }}>
            <GlassCard>
              <CardHeader
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MenuBook sx={{ color: schoolColors.primary }} />
                    <Typography variant="h6" sx={{ color: schoolColors.primary, fontWeight: 600 }}>Applications by Form</Typography>
                  </Box>
                }
                action={
                  <IconButton onClick={() => toggleSection('charts')} sx={{ color: schoolColors.primary }}>
                    {expandedSections.charts ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                }
              />
              <Collapse in={expandedSections.charts}>
                <CardContent>
                  <Box sx={{ height: 300 }}>
                    <Bar data={formChartData} options={chartOptions} />
                  </Box>
                </CardContent>
              </Collapse>
            </GlassCard>
          </motion.div>
        </Grid>

        {/* Status pie */}
        <Grid item xs={12} md={6}>
          <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.7 }}>
            <GlassCard>
              <CardHeader
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DonutLarge sx={{ color: schoolColors.primary }} />
                    <Typography variant="h6" sx={{ color: schoolColors.primary, fontWeight: 600 }}>Status Distribution</Typography>
                  </Box>
                }
                action={
                  <IconButton onClick={() => toggleSection('charts')} sx={{ color: schoolColors.primary }}>
                    {expandedSections.charts ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                }
              />
              <Collapse in={expandedSections.charts}>
                <CardContent>
                  <Box sx={{ height: 300 }}>
                    <Pie data={statusChartData} options={chartOptions} />
                  </Box>
                </CardContent>
              </Collapse>
            </GlassCard>
          </motion.div>
        </Grid>

        {/* Monthly trend line */}
        <Grid item xs={12} md={6}>
          <motion.div initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.8 }}>
            <GlassCard>
              <CardHeader
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Timeline sx={{ color: schoolColors.primary }} />
                    <Typography variant="h6" sx={{ color: schoolColors.primary, fontWeight: 600 }}>Monthly Trend</Typography>
                  </Box>
                }
                action={
                  <IconButton onClick={() => toggleSection('charts')} sx={{ color: schoolColors.primary }}>
                    {expandedSections.charts ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                }
              />
              <Collapse in={expandedSections.charts}>
                <CardContent>
                  <Box sx={{ height: 300 }}>
                    <Line data={monthlyTrendData} options={chartOptions} />
                  </Box>
                </CardContent>
              </Collapse>
            </GlassCard>
          </motion.div>
        </Grid>
      </Grid>

      {/* ── Detailed table ── */}
      <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }}>
        <GlassCard>
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TableChart sx={{ color: schoolColors.primary }} />
                <Typography variant="h6" sx={{ color: schoolColors.primary, fontWeight: 600 }}>Detailed Applications List</Typography>
              </Box>
            }
            action={
              <Chip
                label={`${filteredApplications.length} records`}
                size="small"
                sx={{
                  bgcolor: `${schoolColors.primary}15`,
                  color:   schoolColors.primary,
                  fontWeight: 600,
                  border: `1px solid ${schoolColors.primary}30`,
                }}
              />
            }
          />
          <CardContent>
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    {['Reg No.', 'Name', 'Gender', 'Form', 'Status', 'Submission Date', 'Ward', 'District'].map(col => (
                      <TableCell
                        key={col}
                        sx={{
                          bgcolor:    schoolColors.primary,
                          color:      schoolColors.white,
                          fontWeight: 700,
                          fontSize:   13,
                        }}
                      >
                        {col}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredApplications
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((app) => (
                      <TableRow
                        key={app.id}
                        hover
                        sx={{
                          '&:hover': { bgcolor: `${schoolColors.primary}06` },
                        }}
                      >
                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600, color: schoolColors.primary }}>
                          {app.registration_number}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar
                              sx={{
                                width: 32, height: 32,
                                bgcolor: `linear-gradient(135deg, ${schoolColors.primary}, ${schoolColors.secondary})`,
                                background: `linear-gradient(135deg, ${schoolColors.primary}, ${schoolColors.secondary})`,
                                fontSize: 14, fontWeight: 700,
                              }}
                            >
                              {app.first_name?.[0]}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {app.first_name} {app.last_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {app.middle_name}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            icon={app.gender === 'ME' ? <Male /> : <Female />}
                            label={app.gender === 'ME' ? 'Male' : 'Female'}
                            variant="outlined"
                            sx={{
                              borderColor: app.gender === 'ME' ? schoolColors.secondary : schoolColors.accent,
                              color:       app.gender === 'ME' ? schoolColors.secondary : schoolColors.accent,
                              '& .MuiChip-icon': {
                                color: app.gender === 'ME' ? schoolColors.secondary : schoolColors.accent,
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell>{app.applying_for_level?.replace('form', 'Form ')}</TableCell>
                        <TableCell>{getStatusChip(app.status)}</TableCell>
                        <TableCell>{new Date(app.submitted_date).toLocaleDateString()}</TableCell>
                        <TableCell>{app.ward}</TableCell>
                        <TableCell>{app.district}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={filteredApplications.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, p) => setPage(p)}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              sx={{
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  color: schoolColors.primary,
                },
              }}
            />
          </CardContent>
        </GlassCard>
      </motion.div>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{
            width: '100%',
            ...(snackbar.severity === 'success' && {
              bgcolor: `${schoolColors.primary}f0`,
              color:   schoolColors.white,
              '& .MuiAlert-icon': { color: schoolColors.gold },
            }),
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Reports;
